import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Divider,
  Box,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import type { ConceptoPlanilla, ConceptoFormErrors } from '../types/concepto';
import { initialConcepto, TIPOS_CONCEPTO, TIPOS_CALCULO, TIPOS_REMUNERACION } from '../types/concepto';
import { useConceptoStore } from '../store';
import FormulaEditor from '../../formulas/components/FormulaEditor';

interface EditConceptoProps {
  open: boolean;
  concepto: ConceptoPlanilla | null;
  onClose: () => void;
  fullScreen?: boolean;
}

const EditConcepto: React.FC<EditConceptoProps> = ({ open, concepto, onClose, fullScreen }) => {
  const { saveConcepto, loading, error, clearError } = useConceptoStore();
  const [formData, setFormData] = useState<ConceptoPlanilla>(initialConcepto);
  const [errors, setErrors] = useState<ConceptoFormErrors>({});
  const [tabIndex, setTabIndex] = useState(0);

  const isEdit = !!concepto;
  const isSistema = formData.esConceptoSistema;

  useEffect(() => {
    if (open) {
      setFormData(concepto ? { ...concepto } : { ...initialConcepto });
      setErrors({});
      setTabIndex(0);
      clearError();
    }
  }, [open, concepto, clearError]);

  const handleChange = (field: keyof ConceptoPlanilla, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof ConceptoFormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: ConceptoFormErrors = {};
    if (!formData.codigo.trim()) newErrors.codigo = 'Código es requerido';
    if (!formData.descripcion.trim()) newErrors.descripcion = 'Descripción es requerida';
    if (!formData.tipoConcepto) newErrors.tipoConcepto = 'Tipo es requerido';
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setTabIndex(0);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      await saveConcepto(formData);
    } catch {
      // Error manejado por el store
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
      <DialogTitle>
        {isEdit ? 'Editar Concepto' : 'Nuevo Concepto'}
        {isSistema && (
          <Typography variant="caption" color="warning.main" sx={{ ml: 2 }}>
            (Concepto del sistema - edición limitada)
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 2 }}>
          <Tab label="Datos Generales" />
          <Tab label="Afectaciones" />
        </Tabs>

        {tabIndex === 0 && (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="Código"
                value={formData.codigo}
                onChange={(e) => handleChange('codigo', e.target.value)}
                error={!!errors.codigo}
                helperText={errors.codigo}
                fullWidth
                size="small"
                disabled={isSistema}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="Código PDT"
                value={formData.codigoPDT || ''}
                onChange={(e) => handleChange('codigoPDT', e.target.value || null)}
                fullWidth
                size="small"
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth size="small" error={!!errors.tipoConcepto}>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={formData.tipoConcepto}
                  label="Tipo"
                  onChange={(e) => handleChange('tipoConcepto', e.target.value)}
                  disabled={isSistema}
                >
                  {TIPOS_CONCEPTO.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo Cálculo</InputLabel>
                <Select
                  value={formData.tipoCalculo}
                  label="Tipo Cálculo"
                  onChange={(e) => handleChange('tipoCalculo', e.target.value)}
                >
                  {TIPOS_CALCULO.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Descripción"
                value={formData.descripcion}
                onChange={(e) => handleChange('descripcion', e.target.value)}
                error={!!errors.descripcion}
                helperText={errors.descripcion}
                fullWidth
                size="small"
                inputProps={{ maxLength: 200 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Descripción Corta"
                value={formData.descripcionCorta || ''}
                onChange={(e) => handleChange('descripcionCorta', e.target.value || null)}
                fullWidth
                size="small"
                inputProps={{ maxLength: 200 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="Porcentaje Base"
                type="number"
                value={formData.porcentajeBase ?? ''}
                onChange={(e) => handleChange('porcentajeBase', e.target.value ? Number(e.target.value) : null)}
                fullWidth
                size="small"
                inputProps={{ step: 0.01 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="Orden"
                type="number"
                value={formData.orden}
                onChange={(e) => handleChange('orden', Number(e.target.value) || 0)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormulaEditor
                value={formData.formula || ''}
                onChange={(formula) => handleChange('formula', formula || null)}
                label="Fórmula"
                disabled={isSistema}
                minRows={2}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">Aplica a</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={<Checkbox checked={formData.aplicaEmpleados} onChange={(e) => handleChange('aplicaEmpleados', e.target.checked)} size="small" />}
                  label="Empleados"
                />
                <FormControlLabel
                  control={<Checkbox checked={formData.aplicaObreros} onChange={(e) => handleChange('aplicaObreros', e.target.checked)} size="small" />}
                  label="Obreros"
                />
                <FormControlLabel
                  control={<Checkbox checked={formData.activo} onChange={(e) => handleChange('activo', e.target.checked)} size="small" />}
                  label="Activo"
                />
              </Box>
            </Grid>
          </Grid>
        )}

        {tabIndex === 1 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Marque las afectaciones que aplican a este concepto según legislación peruana
            </Typography>
            <Grid container spacing={1}>
              {[
                { field: 'afectaCTS' as const, label: 'CTS' },
                { field: 'afectaGratificacion' as const, label: 'Gratificación' },
                { field: 'afectaVacaciones' as const, label: 'Vacaciones' },
                { field: 'afectaRenta5ta' as const, label: 'Renta 5ta Categoría' },
                { field: 'afectaEsSalud' as const, label: 'EsSalud' },
                { field: 'afectaPensiones' as const, label: 'Pensiones (AFP/ONP)' },
                { field: 'afectaSCTR' as const, label: 'SCTR' },
                { field: 'afectaUtilidades' as const, label: 'Utilidades' },
                { field: 'afectaSENATI' as const, label: 'SENATI' },
              ].map(({ field, label }) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={field}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData[field]}
                        onChange={(e) => handleChange(field, e.target.checked)}
                        size="small"
                      />
                    }
                    label={label}
                  />
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Afectación de Cálculo
            </Typography>
            <Grid container spacing={1}>
              {[
                { field: 'afectaDiasLaborados' as const, label: 'Días Laborados' },
                { field: 'afectaHorasExtras' as const, label: 'Horas Extras' },
                { field: 'afectaHorasNocturnas' as const, label: 'Horas Nocturnas' },
                { field: 'afectaDiasSubsidiados' as const, label: 'Días Subsidiados' },
                { field: 'afectaTardanzas' as const, label: 'Tardanzas' },
                { field: 'afectaDescuentoSemanal' as const, label: 'Descuento Semanal' },
                { field: 'afectaFeriados' as const, label: 'Feriados' },
                { field: 'afectaQuincena' as const, label: 'Quincena' },
              ].map(({ field, label }) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={field}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData[field]}
                        onChange={(e) => handleChange(field, e.target.checked)}
                        size="small"
                      />
                    }
                    label={label}
                  />
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Renta 5ta
            </Typography>
            <Grid container spacing={1}>
              {[
                { field: 'esProyectada' as const, label: 'Es Proyectada' },
                { field: 'escalaDirecta' as const, label: 'Escala Directa' },
              ].map(({ field, label }) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={field}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData[field]}
                        onChange={(e) => handleChange(field, e.target.checked)}
                        size="small"
                      />
                    }
                    label={label}
                  />
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo Remuneración</InputLabel>
                  <Select
                    value={formData.tipoRemuneracion || ''}
                    label="Tipo Remuneración"
                    onChange={(e) => handleChange('tipoRemuneracion', e.target.value || null)}
                  >
                    <MenuItem value="">
                      <em>Ninguno</em>
                    </MenuItem>
                    {TIPOS_REMUNERACION.map(t => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditConcepto;
